import { useRef, useEffect, useCallback } from 'react';

import { PaymentMethod } from '@stripe/stripe-js';

import {
  useStripeExecuteMutation,
  StripeExecuteTransactionRequest,
  StripeExecuteResponse,
} from '@/api/payments/stripeExecute';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';

const buildExecutePayload = (
  paymentSelectState: PaymentSelectState,
  transactionState: TransactionState,
  client: string,
  accountIdentifier: string,
  paymentMethod: PaymentMethod,
): StripeExecuteTransactionRequest => ({
  client,
  payment_method_id: paymentMethod.id,
  transactionId: transactionState.transactionId,
  account_identifier: accountIdentifier,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
});

export function useStripeExecute() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const transactionState = useAppSelector(state => state.transaction);

  const { getProviderStripeAccountIdentifier } = usePaymentsConfig();

  const stripeAccountIdentifier = getProviderStripeAccountIdentifier();

  const [stripeExecuteTrigger, {
    isLoading,
    data,
    error,
  }] = useStripeExecuteMutation();

  const { getClientId } = useClient();

  const onSuccess = useRef<((data: StripeExecuteResponse) => void) | undefined>(undefined);
  const onRequiresAction = useRef<((data: StripeExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  // Trigger payment-service execute
  const triggerExecuteApi = useCallback(async (
    paymentMethod: PaymentMethod,
    callbacks?: {
      onSuccess?: (data: StripeExecuteResponse) => void,
      onRequiresAction?: (data: StripeExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onRequiresAction.current = callbacks?.onRequiresAction;
    onError.current = callbacks?.onError;

    // Ensure paymentMethod is set
    if (!paymentMethod) {
      logger.error('Stripe paymentMethod not set');
      if (onError.current) {
        onError.current(new Error('Stripe paymentMethod not set'));
      }
      return;
    }

    // Require stripe provider config
    if (!stripeAccountIdentifier) {
      logger.error('Error accessing Stripe config');
      if (onError.current) {
        onError.current(new Error('Error accessing Stripe config'));
      }
      throw new Error('Error accessing Stripe config');
    }

    try {
      const payload = buildExecutePayload(
        paymentSelectState,
        transactionState,
        getClientId(),
        stripeAccountIdentifier,
        paymentMethod,
      );
      await stripeExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Stripe transaction');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }
  }, [paymentSelectState, transactionState, getClientId, stripeAccountIdentifier, stripeExecuteTrigger]);

  // Handle payment-service execute complete
  useEffect(() => {
    // Wait until payment-service execute is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service execute errors
    if (!!error || data?.message !== 'success') {
      logger.error('Error executing Stripe transaction');
      logger.error(error);
      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    // See: https://stripe.com/docs/payments/3d-secure/authentication-flow?locale=en-GB
    // 3DS/SCA Triggering Test Card: 4000002500003155
    if (data?.data?.requires_action) {
      logger.info('Stripe transaction requires action (3DS/SCA)');
      if (onRequiresAction.current) {
        onRequiresAction.current(data);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
    }
  }, [isLoading, data, error, onSuccess, onRequiresAction, onError]);

  return {
    triggerExecuteApi,
    executeIsLoading: isLoading,
    executeData: data,
    executeError: error,
  };
}
